import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";
import * as api from "../services/api";
import {
  connectSocket,
  connectWatchSocket,
  disconnectSocket,
  getSocket,
} from "../services/socket";
import { bestNtpOffset } from "../utils/liveTimeline";
import { detectMismatches, buildSubstitutionPrompt, applySubstitutions } from "../utils/substituteExercises";
import llmApi from "../apis/llmApi";
import { getDefaultModel } from "../config/llmProviders";
import { useToast } from "./ToastContext";
import { openBadges, savedRecently, showEarned } from "../utils/badgeBus";

const SocialContext = createContext(null);

export function useSocial() {
  return useContext(SocialContext);
}

export function useLiveSession() {
  const ctx = useContext(SocialContext);
  return ctx
    ? {
        liveSession: ctx.liveSession,
        createLive: ctx.createLive,
        joinLive: ctx.joinLive,
        watchLive: ctx.watchLive,
        rejoinLive: ctx.rejoinLive,
        leaveLive: ctx.leaveLive,
        startLive: ctx.startLive,
        startLiveWhenJoined: ctx.startLiveWhenJoined,
        sendCmd: ctx.sendCmd,
        loadLive: ctx.loadLive,
        editLivePlan: ctx.editLivePlan,
        extendLive: ctx.extendLive,
        setLobbyReady: ctx.setLobbyReady,
        pauseLive: ctx.pauseLive,
        resumeLive: ctx.resumeLive,
        navigateLive: ctx.navigateLive,
        endLive: ctx.endLive,
        detachDevice: ctx.detachDevice,
        markRepDone: ctx.markRepDone,
        confirmLiveReady: ctx.confirmLiveReady,
        forceSkip: ctx.forceSkip,
        boostRepCap: ctx.boostRepCap,
        setRepCapPaused: ctx.setRepCapPaused,
        substituteExercises: ctx.substituteExercises,
        updateLiveSettings: ctx.updateLiveSettings,
        getServerNow: ctx.getServerNow,
      }
    : null;
}

export function SocialProvider({ children }) {
  const { user } = useAuth();
  const toast = useToast();

  // ─── Friends state ─────────────────────────────────────
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);

  // ─── Groups state ──────────────────────────────────────
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // ─── Feed state ────────────────────────────────────────
  const [feedPosts, setFeedPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedHasMore, setFeedHasMore] = useState(true);

  // ─── Streaks state ─────────────────────────────────────
  const [streaks, setStreaks] = useState({ self: null, friends: [] });

  // ─── Notifications state ───────────────────────────────
  // Everything social used to happen silently: a mate request waited in the
  // Mates tab until you thought to look. Fetched on login and kept live by
  // the notification:new socket event, so the bell doesn't need polling.
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // Bumped when a badge notification arrives, so the Badges card refetches.
  const [badgesVersion, setBadgesVersion] = useState(0);
  // Bumped on training-partner news, so the partner card refetches.
  const [partnerVersion, setPartnerVersion] = useState(0);
  // Bumped by challenge news, so the challenge card reloads.
  const [challengeVersion, setChallengeVersion] = useState(0);
  // The socket effect below subscribes once per user; it reads the toast
  // through a ref rather than resubscribing whenever the toast API changes.
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // ─── Live session state ────────────────────────────────
  // `snapshot` is the latest authoritative live:snapshot from the server's
  // timeline runner — status, block index, absolute deadlines, roster,
  // settings. Everything the follower UI renders derives from it.
  const INITIAL_LIVE = {
    session: null,
    participants: [],
    isHost: false,
    snapshot: null,
    planData: null,
    localPlanData: null,       // Participant's locally-substituted plan tree
    equipmentMismatches: [],   // Array of mismatched exercise nodes
    substitutionStatus: null,  // null | 'loading' | 'done' | 'error'
    substitutionChanges: [],   // Summary of what was changed
    forceSync: true,
    allowAnyonePause: true,
    allowAnyoneNavigate: false,
    // A watcher, not a participant: no roster entry, no controls, no history
    // at the end. Set only by watchLive and never cleared in place — leaving
    // resets to INITIAL_LIVE wholesale.
    isSpectator: false,
    watchState: null,          // null | 'open' | 'finished'
    hostName: null,
    // Phase log carried in from a solo session that went live partway through
    // (#81), so the record saved at the end covers the whole workout and not
    // just the part that happened to be shared. Consumed once by
    // useLiveFollower; null for a session that started live.
    priorPhaseLog: null,
  };
  const [liveSession, setLiveSession] = useState(INITIAL_LIVE);
  const [activeLiveSessions, setActiveLiveSessions] = useState([]);
  const socketRef = useRef(null);
  // Mirror of liveSession for use inside callbacks/effects that should not
  // re-run on every state change (e.g., the resume-toast check on app load).
  // Own live sessions already offered for resume (see loadActiveLiveSessions).
  const resumePromptedRef = useRef(new Set());
  const liveSessionRef = useRef(null);

  // ─── Device state ──────────────────────────────────────
  const [liveDeviceState, setLiveDeviceState] = useState(null);   // latest live:device_state (legacy v2 heartbeat)
  const [devicePresence, setDevicePresence] = useState(null);     // { device_id, online, since_ms }
  // The same events kept per timer. devicePresence is only the latest, and a
  // mate's shared timer reports here too, so its going offline must not read
  // as yours going offline.
  const [presenceByDevice, setPresenceByDevice] = useState({});   // { [device_id]: online }
  // Latest session:metrics relay (HR/watts from a hardware follower or FTMS
  // bridge on the active channel).
  const [channelMetrics, setChannelMetrics] = useState(null);
  const pendingAcksRef = useRef(new Map());                       // req_id → { resolve, reject, timeoutId }
  // NOTE: the v2 bidirectional mirror (deviceCommand + echo-suppress window)
  // is gone. Protocol v3 routes every intent through the server's session
  // channel (session:cmd), so clients never mirror each other directly.

  useEffect(() => {
    liveSessionRef.current = liveSession;
  }, [liveSession]);

  // ─── Load data on auth ────────────────────────────────
  const loadFriends = useCallback(async () => {
    if (!user) return;
    setFriendsLoading(true);
    try {
      const [friendsList, requests, sent] = await Promise.all([
        api.getFriends(),
        api.getFriendRequests(),
        api.getSentFriendRequests(),
      ]);
      setFriends(friendsList);
      setFriendRequests(requests);
      setSentRequests(sent);
    } catch (err) {
      console.error("Failed to load friends:", err);
    } finally {
      setFriendsLoading(false);
    }
  }, [user]);

  const loadGroups = useCallback(async () => {
    if (!user) return;
    setGroupsLoading(true);
    try {
      const groupsList = await api.getGroups();
      setGroups(groupsList);
    } catch (err) {
      console.error("Failed to load groups:", err);
    } finally {
      setGroupsLoading(false);
    }
  }, [user]);

  const loadFeed = useCallback(async (reset = false) => {
    if (!user) return;
    setFeedLoading(true);
    try {
      const before = !reset && feedPosts.length > 0
        ? feedPosts[feedPosts.length - 1].created_at
        : null;
      const posts = await api.getFeed({ limit: 20, before });
      if (reset) {
        setFeedPosts(posts);
      } else {
        setFeedPosts((prev) => [...prev, ...posts]);
      }
      setFeedHasMore(posts.length === 20);
    } catch (err) {
      console.error("Failed to load feed:", err);
    } finally {
      setFeedLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadStreaks = useCallback(async () => {
    if (!user) return;
    try {
      const data = await api.getFriendStreaks();
      setStreaks(data);
    } catch (err) {
      console.error("Failed to load streaks:", err);
    }
  }, [user]);

  const loadActiveLiveSessions = useCallback(async () => {
    if (!user) return;
    try {
      const sessions = await api.getActiveLiveSessions();
      setActiveLiveSessions(sessions);

      // If the user owns a still-live session and isn't currently in one,
      // offer to resume or close it. The server-side sweeper handles truly
      // dead sessions, so anything we see here is a candidate for recovery.
      // Solo channels are hidden from /active — fetch the user's own current
      // session separately and silently re-attach (the wall display may be
      // mid-workout; a refresh must drop the tab straight back in).
      if (!liveSessionRef.current?.session) {
        try {
          const mine = await api.getMyLiveSession();
          if (mine && mine.kind === "solo" && mine.status !== "finished") {
            window.dispatchEvent(
              new CustomEvent("live:resume_request", { detail: { session: mine } }),
            );
          }
        } catch (err) {
          console.error("[live] solo resume check failed:", err);
        }
      }

      const ownActive = sessions.find(
        (s) => s.host_id === user.id && s.status !== "finished",
      );
      // Ask once per session: the list refreshes every 15 s while the feed is
      // open, and a prompt on every refresh stacks up toasts.
      if (
        ownActive &&
        !liveSessionRef.current?.session &&
        !resumePromptedRef.current.has(ownActive.id)
      ) {
        resumePromptedRef.current.add(ownActive.id);
        toast.showToast({
          title: "Resume live session?",
          description: `${ownActive.title || "Your live workout"} is still marked as live.`,
          duration: 15000,
          actions: [
            {
              label: "Continue",
              variant: "default",
              onClick: () => {
                window.dispatchEvent(
                  new CustomEvent("live:resume_request", {
                    detail: { session: ownActive },
                  }),
                );
              },
            },
            {
              label: "Save and close",
              variant: "secondary",
              onClick: () => {
                api.endLiveSession(ownActive.id).catch((err) =>
                  console.error("[live] save & close failed:", err),
                );
                setActiveLiveSessions((prev) =>
                  prev.filter((s) => s.id !== ownActive.id),
                );
              },
            },
          ],
        });
      }
    } catch (err) {
      console.error("Failed to load live sessions:", err);
    }
  }, [user, toast]);

  useEffect(() => {
    if (user) {
      loadFriends();
      loadGroups();
      loadFeed(true);
      loadStreaks();
      loadActiveLiveSessions();
      loadNotifications();
    } else {
      setFriends([]);
      setFriendRequests([]);
      setSentRequests([]);
      setGroups([]);
      setFeedPosts([]);
      setStreaks({ self: null, friends: [] });
      setActiveLiveSessions([]);
      setNotifications([]);
      setUnreadCount(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ─── Device v2 socket listeners (persistent) ───────────
  // Connects the socket on login and listens for ACKs, device_state, and
  // presence events from hardware devices. Independent of live sessions.
  useEffect(() => {
    if (!user) return;
    const socket = connectSocket();
    if (!socket) return;

    const onAck = (data) => {
      const entry = pendingAcksRef.current.get(data.req_id);
      if (entry) {
        clearTimeout(entry.timeoutId);
        pendingAcksRef.current.delete(data.req_id);
        if (data.status === "ok") entry.resolve(data);
        else entry.reject(new Error(data.error || "device_error"));
      }
      // NOTE: intentionally NOT overwriting liveDeviceState.state from ACKs.
      // ACKs for live:play are captured before the IntervalHandler event
      // queue processes Start Interval, so they often carry state:"Inactive"
      // which would falsely flip castState to "idle". The 1 Hz heartbeat is
      // the single source of truth for device state. session_ref from a
      // "Ready" ACK is captured here for completeness only.
      if (data.state === "Ready" && data.session_ref) {
        setLiveDeviceState((prev) => ({
          ...(prev || {}),
          state: prev?.state || "Ready",
          session_ref: data.session_ref,
        }));
      }
    };

    const onDeviceState = (data) => {
      setLiveDeviceState(data);
    };

    const onPresence = (data) => {
      setDevicePresence(data);
      if (data?.device_id != null) {
        setPresenceByDevice((prev) => ({ ...prev, [Number(data.device_id)]: data.online === true }));
      }
      if (!data.online) {
        setLiveDeviceState(null);
      }
    };

    // Protocol v3 command acks: same pendingAcks store as v2, but the ack
    // shape is { req_id, ok, error?, stale?, seq }.
    const onCmdAck = (data) => {
      const entry = pendingAcksRef.current.get(data.req_id);
      if (!entry) return;
      clearTimeout(entry.timeoutId);
      pendingAcksRef.current.delete(data.req_id);
      if (data.ok) entry.resolve(data);
      else entry.reject(Object.assign(new Error(data.error || "cmd_failed"), { stale: data.stale }));
    };

    const onChannelMetrics = (data) => {
      setChannelMetrics({ ...data, receivedAt: Date.now() });
    };

    // Notifications arrive on the same per-user room the device events use
    // (`user_clients:<id>`), so the bell updates while you are looking at it
    // rather than on the next refresh.
    const onNotification = (n) => {
      setNotifications((prev) =>
        prev.some((existing) => existing.id === n.id) ? prev : [n, ...prev],
      );
      setUnreadCount((count) => count + 1);
      // A new badge is worth saying out loud, not just a dot on the bell (#156).
      // Partner news changes the partner card: reload it.
      if (String(n.type).startsWith("partner_")) setPartnerVersion((v) => v + 1);
      // Challenge news changes the challenge card; finishing one is a badge.
      if (String(n.type).startsWith("challenge_")) {
        setChallengeVersion((v) => v + 1);
        if (n.badge_keys?.length) setBadgesVersion((v) => v + 1);
        if (n.type === "challenge_completed" || n.type === "challenge_mate_passed") {
          toastRef.current?.showToast({
            title: n.message,
            duration: 8000,
            action: { label: "See it", onClick: openBadges },
          });
          return;
        }
      }
      if (n.type === "badges_earned") {
        setBadgesVersion((v) => v + 1);
        // Earned by a session just saved: the finish sheet shows them (#204).
        if (savedRecently() && n.badge_keys?.length) {
          showEarned(n.badge_keys);
          return;
        }
        toastRef.current?.showToast({
          title: n.message,
          duration: 8000,
          action: { label: "See them", onClick: openBadges },
        });
      }
    };

    socket.on("live:ack", onAck);
    socket.on("live:device_state", onDeviceState);
    socket.on("device:presence", onPresence);
    socket.on("session:cmd_ack", onCmdAck);
    socket.on("session:metrics", onChannelMetrics);
    socket.on("notification:new", onNotification);

    const pendingAcks = pendingAcksRef.current;
    return () => {
      socket.off("live:ack", onAck);
      socket.off("live:device_state", onDeviceState);
      socket.off("device:presence", onPresence);
      socket.off("session:cmd_ack", onCmdAck);
      socket.off("session:metrics", onChannelMetrics);
      socket.off("notification:new", onNotification);
      // Reject any pending ACKs
      for (const [, entry] of pendingAcks) {
        clearTimeout(entry.timeoutId);
        entry.reject(new Error("unmounted"));
      }
      pendingAcks.clear();
    };
  }, [user]);

  // ─── Device v2 cast helpers ────────────────────────────
  const castEmit = useCallback((event, payload = {}) => {
    const socket = connectSocket();
    if (!socket) return Promise.reject(new Error("no_socket"));
    const reqId = crypto.randomUUID().slice(0, 12);
    const fullPayload = { ...payload, req_id: reqId };
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        pendingAcksRef.current.delete(reqId);
        reject(new Error("ack_timeout"));
      }, 5000);
      pendingAcksRef.current.set(reqId, { resolve, reject, timeoutId });
      socket.emit(event, fullPayload);
    });
  }, []);

  const castLoad = useCallback((plan, sessionRef, title) => {
    return castEmit("live:cast_load", {
      session_ref: sessionRef || null,
      source: "web",
      title: title || plan?.label || "Workout",
      auto_start: false,
      plan,
    });
  }, [castEmit]);

  const castPlay = useCallback(() => castEmit("live:cast_play", { from: "ready" }), [castEmit]);
  const castPause = useCallback(() => castEmit("live:cast_pause"), [castEmit]);
  const castSkip = useCallback((direction = 1) => castEmit("live:cast_skip", { direction }), [castEmit]);
  const castStop = useCallback(() => castEmit("live:cast_stop", { reason: "user" }), [castEmit]);

  // ─── Protocol v3: session channel command ──────────────
  // Every transport intent (from the web UI or, on the firmware side, the
  // USB remote) is a command TO the server; the runner mutates state and
  // broadcasts fresh snapshots to all followers. Returns the ack promise.
  const sendCmd = useCallback((cmd, args = {}) => {
    const socket = connectSocket();
    if (!socket) return Promise.reject(new Error("no_socket"));
    const reqId = crypto.randomUUID().replace(/-/g, "").slice(0, 12);
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        pendingAcksRef.current.delete(reqId);
        reject(new Error("ack_timeout"));
      }, 5000);
      pendingAcksRef.current.set(reqId, { resolve, reject, timeoutId });
      socket.emit("session:cmd", { v: 3, req_id: reqId, cmd, args });
    });
  }, []);

  // Stage a timeline on the channel without starting it (device shows Ready;
  // play from either side then starts the staged plan). `target` picks one
  // timer ({ device_id, take_over }) instead of every timer you own (#171).
  const loadLive = useCallback(
    (timeline, settings = {}, target = {}) => sendCmd("load", { timeline, settings, ...target }),
    [sendCmd],
  );

  // Swap in an edited plan while paused (#175): the tree for display and the
  // recompiled timeline the server runs, plus where the person now stands.
  const editLivePlan = useCallback(
    (plan, timeline, { atIndex, sameBlock }) =>
      sendCmd("edit", { plan, timeline, atIndex, sameBlock }),
    [sendCmd],
  );

  // Keep going after the finish (#199): the plan with a block added at the
  // end, its timeline, and where the added block starts in it.
  const extendLive = useCallback(
    (plan, timeline, atIndex) => sendCmd("extend", { plan, timeline, atIndex }),
    [sendCmd],
  );

  // Report this participant's live HR up the session channel (issue #38). The
  // server records it, relays it to the room, and folds the latest value into
  // the hardware follower's session:state. Fire-and-forget; throttle the caller.
  const reportLiveMetrics = useCallback((hrBpm) => {
    const bpm = Number(hrBpm);
    if (!Number.isFinite(bpm) || bpm <= 0) return;
    const socket = getSocket();
    if (socket) socket.emit("session:metrics", { v: 3, hr_bpm: Math.round(bpm) });
  }, []);

  // ─── Notification actions ──────────────────────────────
  const loadNotifications = useCallback(async () => {
    try {
      const data = await api.getNotifications();
      setNotifications(data?.items || []);
      setUnreadCount(data?.unread || 0);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    }
  }, []);

  // Reading is not dismissing: the list stays so you can still see what
  // happened, only the count clears.
  const markNotificationsRead = useCallback(async () => {
    setUnreadCount(0);
    setNotifications((prev) =>
      prev.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() })),
    );
    try {
      await api.markNotificationsRead();
    } catch (err) {
      console.error("Failed to mark notifications read:", err);
      loadNotifications();
    }
  }, [loadNotifications]);

  // ─── Friend actions ────────────────────────────────────
  const sendRequest = useCallback(async (addresseeId) => {
    await api.sendFriendRequest(addresseeId);
    await loadFriends();
  }, [loadFriends]);

  const acceptRequest = useCallback(async (friendshipId) => {
    await api.acceptFriendRequest(friendshipId);
    await loadFriends();
  }, [loadFriends]);

  const rejectRequest = useCallback(async (friendshipId) => {
    await api.rejectFriendRequest(friendshipId);
    await loadFriends();
  }, [loadFriends]);

  const removeFriendAction = useCallback(async (friendshipId) => {
    await api.removeFriend(friendshipId);
    await loadFriends();
  }, [loadFriends]);

  // ─── Feed actions ──────────────────────────────────────
  const handleToggleLike = useCallback(async (postId) => {
    const result = await api.toggleLike(postId);
    setFeedPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              liked_by_me: result.liked,
              like_count: parseInt(p.like_count) + (result.liked ? 1 : -1),
            }
          : p,
      ),
    );
    return result;
  }, []);

  const handleCreatePost = useCallback(async (data) => {
    const post = await api.createPost(data);
    await loadFeed(true);
    return post;
  }, [loadFeed]);

  const handleDeletePost = useCallback(async (postId) => {
    await api.deletePostApi(postId);
    setFeedPosts((prev) => prev.filter((p) => p.id !== postId));
  }, []);

  // ─── Trained together ──────────────────────────────────
  // Every one of these ends by reloading the feed rather than patching the
  // post in place: a link shows on both people's cards and the server decides
  // which sessions it actually joined, so the local guess would be wrong as
  // often as not.
  const handleLinkSessions = useCallback(async (payload) => {
    const link = await api.linkSessions(payload);
    await loadFeed(true);
    return link;
  }, [loadFeed]);

  const handleRespondToSessionLink = useCallback(async (linkId, accept) => {
    const link = await api.respondToSessionLink(linkId, accept);
    await Promise.all([loadFeed(true), loadNotifications()]);
    return link;
  }, [loadFeed, loadNotifications]);

  const handleUnlinkSessions = useCallback(async (linkId) => {
    await api.unlinkSessions(linkId);
    await loadFeed(true);
  }, [loadFeed]);

  // ─── Live session clock sync (NTP-style over the socket) ──────────
  // The naive serverNow-Date.now() offset is biased by one-way latency;
  // the ping-pong strips transport time. Lowest-RTT of 3 samples wins.
  const clockOffsetRef = useRef(0);
  const getServerNow = useCallback(
    () => Date.now() + clockOffsetRef.current,
    [],
  );
  const runTimeSync = useCallback(async () => {
    const socket = getSocket();
    if (!socket) return;
    const samples = [];
    for (let i = 0; i < 3; i++) {
      // eslint-disable-next-line no-await-in-loop
      const sample = await new Promise((resolve) => {
        const t0 = Date.now();
        const timeoutId = setTimeout(() => {
          socket.off("live:time_sync", onReply);
          resolve(null);
        }, 2000);
        const onReply = (data) => {
          if (data?.t0 !== t0) return;
          clearTimeout(timeoutId);
          socket.off("live:time_sync", onReply);
          resolve({ t0, t1: data.t1, t2: data.t2, t3: Date.now() });
        };
        socket.on("live:time_sync", onReply);
        socket.emit("live:time_sync", { t0 });
      });
      if (sample) samples.push(sample);
    }
    if (samples.length) clockOffsetRef.current = bestNtpOffset(samples);
  }, []);

  // ─── Live session actions ─────────────────────────────
  const setupSocketListeners = useCallback((socket) => {
    // Authoritative state snapshot from the server's timeline runner —
    // broadcast on every transition and replayed instantly on join/reconnect.
    socket.on("live:snapshot", (snap) => {
      setLiveSession((prev) => {
        // A watcher's session object is the public projection, which carries
        // no id to match against (the server never tells them one). Their
        // socket only ever belongs to the one `watch:` room, so any snapshot
        // reaching it is by construction the session they asked to watch.
        if (!prev.session) return prev;
        if (
          !prev.isSpectator &&
          Number(snap.sessionId) !== Number(prev.session.id)
        ) {
          return prev;
        }
        // Out-of-order delivery guard: never apply an older snapshot.
        if (prev.snapshot && snap.seq < prev.snapshot.seq) return prev;
        const statusMap = {
          lobby: "waiting",
          active: "active",
          waiting_for_sync: "active",
          paused: "paused",
          ended: "finished",
        };
        return {
          ...prev,
          snapshot: snap,
          session: {
            ...prev.session,
            status: statusMap[snap.status] || prev.session.status,
          },
          participants: snap.participants?.length
            ? snap.participants
            : prev.participants,
          forceSync: snap.settings?.forceSync ?? prev.forceSync,
          allowAnyonePause:
            snap.settings?.allowAnyonePause ?? prev.allowAnyonePause,
          allowAnyoneNavigate:
            snap.settings?.allowAnyoneNavigate ?? prev.allowAnyoneNavigate,
        };
      });
    });

    // The host edited the plan while paused (#175). Arrives just before the
    // snapshot that moves onto the new timeline, so every block resolves
    // against the edited tree. Edits are refused once partners join, so there
    // is no substituted copy to preserve: both trees take the edit.
    //
    // Keep going (#199) is allowed with partners: it only adds blocks at the
    // end, so a partner's substituted copy keeps its own steps and takes the
    // new tail.
    socket.on("live:plan", ({ plan_data: planData, extended = false } = {}) => {
      if (!planData) return;
      setLiveSession((prev) => {
        if (!prev.session) return prev;
        const local = prev.localPlanData;
        const localPlanData =
          extended && local && Array.isArray(local.children)
            ? {
                ...local,
                children: [
                  ...local.children,
                  ...(planData.children || []).slice(local.children.length),
                ],
              }
            : planData;
        return { ...prev, planData, localPlanData };
      });
    });

    // Legacy/sweeper end path. Runner sessions deliver an ended snapshot
    // (which drives the save-to-history flow), so only hard-reset here when
    // no ended snapshot is in play (e.g. a lobby swept while idle).
    socket.on("live:finished", () => {
      setLiveSession((prev) => {
        if (prev.snapshot && prev.snapshot.status === "ended") return prev;
        if (prev.snapshot && prev.session?.status !== "waiting") return prev;
        disconnectSocket();
        return { ...INITIAL_LIVE };
      });
    });

    socket.on("live:error", (data) => {
      console.error("Live session error:", data.message);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createLive = useCallback(async (data, { priorPhaseLog = null } = {}) => {
    const session = await api.createLiveSession(data);
    const socket = connectSocket();
    if (socket) {
      socketRef.current = socket;
      setupSocketListeners(socket);
      socket.emit("live:join", { joinCode: session.join_code });
      runTimeSync();
    }
    setLiveSession({
      ...INITIAL_LIVE,
      session, isHost: true,
      planData: data.plan_data || null,
      localPlanData: data.plan_data || null,
      forceSync: data.force_sync !== false,
      allowAnyonePause: data.allow_anyone_pause !== false,
      allowAnyoneNavigate: data.allow_anyone_navigate === true,
      priorPhaseLog:
        Array.isArray(priorPhaseLog) && priorPhaseLog.length > 0
          ? [...priorPhaseLog]
          : null,
    });
    return session;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupSocketListeners, runTimeSync]);

  /**
   * Follow a live session read-only, with or without an account.
   *
   * Deliberately does NOT go through joinLive: there is no REST handshake, no
   * participant row and no roster entry, so the session object here is the
   * public projection (`api.getWatchSession`) rather than a real live_sessions
   * row — it has no id and no join code, and nothing downstream should expect
   * either. `isSpectator` is what the UI branches on.
   */
  const watchLive = useCallback(async (watchCode) => {
    const view = await api.getWatchSession(watchCode);

    // A link opened after the workout ended: say so rather than connecting a
    // socket to a session that will never send another snapshot.
    if (view.state === "finished") {
      setLiveSession({
        ...INITIAL_LIVE,
        isSpectator: true,
        watchState: "finished",
        hostName: view.host_name || null,
        session: { title: view.title, status: "finished" },
      });
      return view;
    }

    const socket = connectWatchSocket(watchCode);
    if (socket) {
      socketRef.current = socket;
      setupSocketListeners(socket);
      socket.emit("live:watch");
      runTimeSync();
    }

    setLiveSession({
      ...INITIAL_LIVE,
      isSpectator: true,
      watchState: "open",
      hostName: view.host_name || null,
      session: {
        title: view.title,
        status: view.status,
        kind: "shared",
        mode: view.mode,
      },
      planData: view.plan_data || null,
      // No local substitution pass for a watcher: they are following what the
      // host is doing, not adapting it to equipment they may not even have.
      localPlanData: view.plan_data || null,
    });
    return view;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupSocketListeners, runTimeSync]);

  const joinLive = useCallback(async (joinCode) => {
    const result = await api.joinLiveSessionApi(joinCode);
    const socket = connectSocket();
    if (socket) {
      socketRef.current = socket;
      setupSocketListeners(socket);
      socket.emit("live:join", { joinCode });
      runTimeSync();
    }
    setLiveSession({
      ...INITIAL_LIVE,
      session: result.session,
      participants: result.participants,
      isHost: false,
      planData: result.session?.plan_data || null,
      localPlanData: result.session?.plan_data || null,
      forceSync: result.session?.force_sync !== false,
      allowAnyonePause: result.session?.allow_anyone_pause !== false,
      allowAnyoneNavigate: result.session?.allow_anyone_navigate === true,
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupSocketListeners, runTimeSync]);

  // Rejoin an active session after page refresh (user is already a participant in DB)
  const rejoinLive = useCallback(async (session) => {
    const socket = connectSocket();
    if (socket) {
      socketRef.current = socket;
      setupSocketListeners(socket);
      socket.emit("live:join", { joinCode: session.join_code });
      runTimeSync();
    }
    const isHost = session.host_id === user?.id;
    setLiveSession({
      ...INITIAL_LIVE,
      session,
      isHost,
      planData: session.plan_data || null,
      localPlanData: session.plan_data || null,
      forceSync: session.force_sync !== false,
      allowAnyonePause: session.allow_anyone_pause !== false,
      allowAnyoneNavigate: session.allow_anyone_navigate === true,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupSocketListeners, runTimeSync, user]);

  const leaveLive = useCallback(() => {
    const socket = getSocket();
    if (socket) {
      socket.emit("live:leave");
    }
    setLiveSession({ ...INITIAL_LIVE });
    disconnectSocket();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Host starts the session: uploads the compiled timeline (structure +
  // timing only) which hands ownership to the server's timeline runner.
  const startLive = useCallback((timeline, settings = {}, { atIndex } = {}) => {
    const socket = getSocket();
    if (socket) socket.emit("live:start", { timeline, settings, atIndex });
  }, []);

  // Start as soon as the socket is genuinely in the session.
  //
  // createLive emits live:join and returns; the server sets socket.liveSessionId
  // inside that handler, after its own awaits — and socket.io does not wait for
  // one handler to finish before dispatching the next. So a live:start sent
  // straight after createLive can arrive mid-join and be refused as "only the
  // host can start". The join broadcasts a snapshot, which is the proof it
  // landed. Only the go-live-from-here path needs this; the lobby's Start
  // button is pressed long after the join.
  const startLiveWhenJoined = useCallback(
    (timeline, settings = {}, { atIndex, timeoutMs = 8000 } = {}) =>
      new Promise((resolve, reject) => {
        const socket = getSocket();
        if (!socket) return reject(new Error("offline"));
        let settled = false;
        const fire = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          socket.off("live:snapshot", fire);
          socket.emit("live:start", { timeline, settings, atIndex });
          resolve();
        };
        const timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          socket.off("live:snapshot", fire);
          reject(new Error("join_timeout"));
        }, timeoutMs);
        socket.on("live:snapshot", fire);
      }),
    [],
  );

  const setLobbyReady = useCallback((ready) => {
    const socket = getSocket();
    if (socket) socket.emit("live:lobby_ready", { ready: ready === true });
  }, []);

  const pauseLive = useCallback(() => {
    const socket = getSocket();
    if (socket) socket.emit("live:pause");
  }, []);

  const resumeLive = useCallback(() => {
    const socket = getSocket();
    if (socket) socket.emit("live:resume");
  }, []);

  // expectedBlockIndex makes skips idempotent: a tap that raced a block
  // change carries a stale index and is silently dropped server-side.
  const navigateLive = useCallback((direction, expectedBlockIndex) => {
    const socket = getSocket();
    if (!socket) return;
    const dir =
      direction === "prev" || direction === -1 || direction < 0 ? -1 : 1;
    socket.emit("live:navigate", { direction: dir, expectedBlockIndex });
  }, []);

  // Host ends the session. State is NOT reset here — the server broadcasts
  // an ended snapshot which drives the per-participant save-to-history flow;
  // leaveLive() clears state once the user has saved or dismissed.
  const endLive = useCallback(async () => {
    const sessionId = liveSession.session?.id;
    const socket = getSocket();
    if (socket) socket.emit("live:end");
    // Also update via REST to ensure DB is marked finished
    if (sessionId) {
      try {
        await api.endLiveSession(sessionId);
      } catch (err) {
        console.error("[Khronos] REST endLive fallback failed:", err);
      }
    }
  }, [liveSession.session]);

  // Detach the user's hardware device(s) from the current live session WITHOUT
  // ending it. The server tells the device to disengage (return to idle) and
  // drops it from the runner; this web client keeps following the session so
  // the user can finish the workout on screen.
  // Send the session already under way to this user's hardware — the
  // pause-screen twin of the setup screen's "Send to Device", for a display
  // you didn't think to use before starting. Resolves with the server's
  // account of what it reached: { invited, attached }.
  //
  // afterJoin: straight after createLive the socket may not be in the session
  // yet (see startLiveWhenJoined), so wait for its first snapshot before
  // sending. Used when a timer session goes live and the timer comes along.
  const sendSessionToDevice = useCallback(({ afterJoin = false } = {}) => {
    const socket = getSocket();
    if (!socket) return Promise.reject(new Error("offline"));
    return new Promise((resolve, reject) => {
      const onSent = (data) => {
        clearTimeout(timeoutId);
        socket.off("device:sent", onSent);
        if (data?.ok) resolve(data);
        else reject(new Error(data?.error || "send_failed"));
      };
      const timeoutId = setTimeout(() => {
        socket.off("live:snapshot", send);
        socket.off("device:sent", onSent);
        reject(new Error("timeout"));
      }, afterJoin ? 10000 : 5000);
      const send = () => {
        socket.off("live:snapshot", send);
        socket.on("device:sent", onSent);
        socket.emit("live:send_to_device");
      };
      if (afterJoin) socket.on("live:snapshot", send);
      else send();
    });
  }, []);

  const detachDevice = useCallback(() => {
    const socket = getSocket();
    if (socket) socket.emit("device:detach");
  }, []);

  // Cast-only sync: emits coach state to the user's own hardware devices
  // without forming a live session. Used by "Send to Device" so the device
  // clock stays locked to the web app's coach view via drift correction.
  const castSync = useCallback((stateData) => {
    const socket = connectSocket();
    if (socket) {
      socket.emit("live:cast_sync", stateData);
    }
  }, []);

  // "Ready" during a live changeover hold.
  const confirmLiveReady = useCallback(() => {
    const socket = getSocket();
    if (socket) socket.emit("live:ready");
  }, []);

  const markRepDone = useCallback(() => {
    const socket = getSocket();
    if (socket) socket.emit("live:rep_done");
  }, []);

  const forceSkip = useCallback(() => {
    const socket = getSocket();
    if (socket && liveSession.isHost) socket.emit("live:force_skip");
  }, [liveSession.isHost]);

  // Rep-barrier auto-advance controls — open to any participant.
  const boostRepCap = useCallback(() => {
    const socket = getSocket();
    if (socket) socket.emit("live:rep_cap_boost");
  }, []);

  const setRepCapPaused = useCallback((paused) => {
    const socket = getSocket();
    if (socket) socket.emit("live:rep_cap_pause", { paused: paused === true });
  }, []);

  const updateLiveSettings = useCallback((settings) => {
    const socket = getSocket();
    if (socket && liveSession.isHost) {
      socket.emit("live:update_settings", settings);
    }
  }, [liveSession.isHost]);

  // AI-driven exercise substitution for equipment mismatches
  const substituteExercises = useCallback(async (localEquipment) => {
    const planData = liveSession.planData;
    if (!planData || !localEquipment?.length) return;

    // Detect mismatches
    const mismatches = detectMismatches(planData, localEquipment);
    setLiveSession((prev) => ({ ...prev, equipmentMismatches: mismatches }));

    if (mismatches.length === 0) {
      setLiveSession((prev) => ({ ...prev, substitutionStatus: "done", substitutionChanges: [] }));
      return;
    }

    // Call AI
    setLiveSession((prev) => ({ ...prev, substitutionStatus: "loading" }));
    try {
      // Get LLM settings from localStorage (same source as workout generation)
      const stored = JSON.parse(localStorage.getItem("llmSettings") || "{}");
      const settings = {
        provider: stored.provider || "google",
        model: stored.model || getDefaultModel(stored.provider || "google"),
        apiKey: stored.apiKey || stored.apiKeys?.[stored.provider] || "",
        apiKeys: stored.apiKeys || {},
      };

      const prompt = buildSubstitutionPrompt(mismatches, localEquipment);
      const substitutions = await llmApi.generateExerciseSubstitutions(prompt, settings);
      const { plan, changes } = applySubstitutions(planData, substitutions);

      setLiveSession((prev) => ({
        ...prev,
        localPlanData: plan,
        substitutionStatus: "done",
        substitutionChanges: changes,
      }));
    } catch (err) {
      console.error("[Khronos] Exercise substitution failed:", err);
      setLiveSession((prev) => ({
        ...prev,
        substitutionStatus: "error",
        // Keep localPlanData = planData (unmodified) so the session can still proceed
      }));
    }
  }, [liveSession.planData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectSocket();
    };
  }, []);

  // NOTE: the old pagehide force-end handler is intentionally gone. The
  // server's timeline runner owns session lifetime now: a host whose tab
  // hides or drops simply disconnects, gets a 60 s reconnect grace, and the
  // session auto-pauses (never instant-ends) if they stay away.

  // Lightweight keep-alive from every participant (was host-only). Bumps
  // live_sessions.updated_at and refreshes the clock offset on a slow timer.
  useEffect(() => {
    const sessionId = liveSession.session?.id;
    if (!sessionId) return;

    const tick = () => {
      if (document.visibilityState !== "visible") return;
      const socket = getSocket();
      if (socket) socket.emit("live:heartbeat");
      runTimeSync();
    };
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, [liveSession.session?.id, runTimeSync]);

  // Reconnect recovery: when socket.io re-establishes the connection while
  // we hold a session, re-join the room — the server replies with a fresh
  // snapshot (instant state replay, no spinner).
  useEffect(() => {
    const joinCode = liveSession.session?.join_code;
    if (!joinCode) return;
    const socket = getSocket();
    if (!socket) return;

    const onConnect = () => {
      socket.emit("live:join", { joinCode });
      runTimeSync();
    };
    socket.on("connect", onConnect);
    return () => socket.off("connect", onConnect);
  }, [liveSession.session?.join_code, runTimeSync]);

  const value = {
    // Friends
    friends,
    friendRequests,
    sentRequests,
    friendsLoading,
    loadFriends,
    sendRequest,
    acceptRequest,
    rejectRequest,
    removeFriend: removeFriendAction,
    // Groups
    groups,
    groupsLoading,
    loadGroups,
    // Feed
    feedPosts,
    feedLoading,
    feedHasMore,
    loadFeed,
    createPost: handleCreatePost,
    deletePost: handleDeletePost,
    toggleLike: handleToggleLike,
    // Trained together
    linkSessions: handleLinkSessions,
    respondToSessionLink: handleRespondToSessionLink,
    unlinkSessions: handleUnlinkSessions,
    // Streaks
    streaks,
    loadStreaks,
    // Notifications
    notifications,
    unreadCount,
    loadNotifications,
    markNotificationsRead,
    badgesVersion,
    partnerVersion,
    challengeVersion,
    // Live
    liveSession,
    activeLiveSessions,
    loadActiveLiveSessions,
    createLive,
    joinLive,
    watchLive,
    rejoinLive,
    leaveLive,
    startLive,
    startLiveWhenJoined,
    setLobbyReady,
    pauseLive,
    resumeLive,
    navigateLive,
    endLive,
    detachDevice,
    getServerNow,
    castSync,
    // Device + session channel (v3)
    liveDeviceState,
    devicePresence,
    presenceByDevice,
    sendSessionToDevice,
    channelMetrics,
    sendCmd,
    loadLive,
    editLivePlan,
    extendLive,
    reportLiveMetrics,
    // Legacy v2 cast events (standalone device views only; removed with the
    // v2 fleet)
    castLoad,
    castPlay,
    castPause,
    castSkip,
    castStop,
    markRepDone,
    confirmLiveReady,
    forceSkip,
    boostRepCap,
    setRepCapPaused,
    substituteExercises,
    updateLiveSettings,
  };

  return <SocialContext.Provider value={value}>{children}</SocialContext.Provider>;
}
